import React, { useEffect, useState } from 'react';
import './PlayerName.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

// ─── Types ───────────────────────────────────────────────

export interface DecorationData {
  id: string;
  emoji: string;
  cssClass: string;
  gradient: string;
}

interface PlayerNameProps {
  userId: string;
  userName: string;
  /** Pass decoration directly if already known */
  decoration?: DecorationData | null;
  /** Legacy: if true, uses old Christmas effect */
  hasChristmasEffect?: boolean;
  /** Fetch decoration from API if not provided */
  checkEffect?: boolean;
  className?: string;
}

// ─── Cache ───────────────────────────────────────────────

const decorationsCache: Record<string, DecorationData | null> = {};

// ─── Component ───────────────────────────────────────────

const PlayerName: React.FC<PlayerNameProps> = ({
  userId,
  userName,
  decoration: decorationProp,
  hasChristmasEffect,
  checkEffect = false,
  className = '',
}) => {
  const [decoration, setDecoration] = useState<DecorationData | null>(
    decorationProp ?? null
  );
  const [loading, setLoading] = useState(false);

  // Legacy compat: convert hasChristmasEffect to decoration
  useEffect(() => {
    if (hasChristmasEffect) {
      setDecoration({
        id: 'snowflake_2025',
        emoji: '❄️',
        cssClass: 'deco-snowflake',
        gradient: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 25%, #4facfe 50%, #00f2fe 75%, #4facfe 100%)',
      });
      return;
    }

    if (decorationProp !== undefined) {
      setDecoration(decorationProp);
      return;
    }

    // Check cache
    if (decorationsCache[userId] !== undefined) {
      setDecoration(decorationsCache[userId]);
      return;
    }

    // Fetch from new API
    if (checkEffect && userId) {
      setLoading(true);
      fetch(`${API_URL}/api/decorations/user/${userId}`)
        .then(res => res.json())
        .then(data => {
          const deco = data.decoration || null;
          decorationsCache[userId] = deco;
          setDecoration(deco);
        })
        .catch(() => {
          decorationsCache[userId] = null;
          setDecoration(null);
        })
        .finally(() => setLoading(false));
    }
  }, [userId, hasChristmasEffect, decorationProp, checkEffect]);

  if (loading) {
    return <span className={`player-name ${className}`}>{userName}</span>;
  }

  if (decoration) {
    return (
      <span
        className={`player-name player-deco ${decoration.cssClass} ${className}`}
      >
        <span className="deco-icon-left">{decoration.emoji}</span>
        <span
          className="deco-name-text"
          style={
            decoration.gradient
              ? ({
                  '--deco-gradient': decoration.gradient,
                } as React.CSSProperties)
              : undefined
          }
        >
          {userName}
        </span>
        <span className="deco-icon-right">{decoration.emoji}</span>
      </span>
    );
  }

  return <span className={`player-name ${className}`}>{userName}</span>;
};

// ─── Hooks ───────────────────────────────────────────────

/** Batch-load decorations for a list of user IDs (leaderboards, etc.) */
export const usePlayerDecorations = (userIds: string[]) => {
  const [decorations, setDecorations] = useState<Record<string, DecorationData | null>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userIds.length) return;

    const uncachedIds = userIds.filter(id => decorationsCache[id] === undefined);
    const cached: Record<string, DecorationData | null> = {};
    userIds.forEach(id => {
      if (decorationsCache[id] !== undefined) {
        cached[id] = decorationsCache[id];
      }
    });

    if (!uncachedIds.length) {
      setDecorations(cached);
      return;
    }

    setLoading(true);
    fetch(`${API_URL}/api/decorations/users/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds: uncachedIds }),
    })
      .then(res => res.json())
      .then(data => {
        const newDecos = data.decorations || {};
        uncachedIds.forEach(id => {
          decorationsCache[id] = newDecos[id] || null;
        });
        setDecorations({ ...cached, ...newDecos });
      })
      .catch(() => {
        uncachedIds.forEach(id => {
          decorationsCache[id] = null;
        });
        setDecorations(cached);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(',')]);

  return { decorations, loading };
};

/** Legacy compat: re-export under old name */
export const useChristmasEffects = (userIds: string[]) => {
  const { decorations, loading } = usePlayerDecorations(userIds);
  const effects: Record<string, boolean> = {};
  Object.entries(decorations).forEach(([id, deco]) => {
    effects[id] = deco !== null;
  });
  return { effects, loading };
};

/** Legacy compat */
export const preloadChristmasEffects = async (userIds: string[]) => {
  const uncachedIds = userIds.filter(id => decorationsCache[id] === undefined);
  if (!uncachedIds.length) return;
  try {
    const res = await fetch(`${API_URL}/api/decorations/users/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds: uncachedIds }),
    });
    const data = await res.json();
    uncachedIds.forEach(id => {
      decorationsCache[id] = data.decorations?.[id] || null;
    });
  } catch {
    uncachedIds.forEach(id => {
      decorationsCache[id] = null;
    });
  }
};

/** Invalidate cache for a user after they change decoration */
export const invalidateDecorationCache = (userId: string) => {
  delete decorationsCache[userId];
};

export default PlayerName;
